import React, { useState, useEffect, useRef } from "react";
import { 
    Card, Row, Col, Button, Badge, Table, Spinner, Alert, 
    Modal, Form, InputGroup, Tooltip, OverlayTrigger 
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate, formatNumber } from "../utils/formatters";
import { STATUT_FACTURE, MODE_REGLEMENT } from "../utils/constantes";
import { API_BASE_URL } from "../constants/api";
import logo from "../assets/img/IMG-20250130-WA0008.jpg";
import './Factures.css';

export default function DetailFacture() {
    const navigate = useNavigate();
    const { id } = useParams();
    const printRef = useRef();
    
    const [facture, setFacture] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showPaiementModal, setShowPaiementModal] = useState(false);
    const [showAvoirModal, setShowAvoirModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [generatingPDF, setGeneratingPDF] = useState(false);
    const [societeInfos, setSocieteInfos] = useState(null);
    const [logoBase64, setLogoBase64] = useState('');
    
    const [taxes, setTaxes] = useState({
        tva_taux: 18,
        css_taux: 1,
        tva_active: true,
        css_active: true
    });
    
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [paiement, setPaiement] = useState({
        montant: '',
        mode_reglement: 'especes',
        reference: '',
        date_reglement: new Date().toISOString().split('T')[0]
    });

    const [avoir, setAvoir] = useState({
        motif: '',
        lignes: []
    });

    useEffect(() => {
        fetchFacture();
        fetchTaxes();
        fetchSocieteInfos();
        loadLogoBase64();
    }, [id]);

    const fetchSocieteInfos = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=societe`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setSocieteInfos(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement société", err);
        }
    };

    const loadLogoBase64 = () => {
        return new Promise((resolve) => {
            try {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    const dataURL = canvas.toDataURL('image/jpeg');
                    setLogoBase64(dataURL);
                    resolve(dataURL);
                };
                img.onerror = () => {
                    console.warn("Impossible de charger le logo");
                    setLogoBase64('');
                    resolve('');
                };
                img.src = logo;
            } catch (error) {
                console.error("Erreur chargement logo:", error);
                setLogoBase64('');
                resolve('');
            }
        });
    };

    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=taxes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setTaxes(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };

    const fetchFacture = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setFacture(result.data);
            
            if (result.data?.lignes) {
                setAvoir({
                    ...avoir,
                    lignes: result.data.lignes.map(l => ({
                        ...l,
                        selected: true,
                        quantite_avoir: l.quantite
                    }))
                });
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const handlePaiementSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            const id_utilisateur = user?.id || user?.id_utilisateur;

            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php`,
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'encaisser',
                        id_facture: id,
                        montant: parseFloat(paiement.montant),
                        mode_reglement: paiement.mode_reglement,
                        reference: paiement.reference,
                        id_utilisateur: id_utilisateur
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowPaiementModal(false);
                fetchFacture();
                showSuccess("Paiement enregistré avec succès");
            } else {
                showError(result.message || "Erreur lors du paiement");
            }
        } catch (err) {
            showError("Erreur de connexion");
        } finally {
            setSaving(false);
        }
    };

    const handleAvoirSubmit = async (e) => {
        e.preventDefault();
        
        const lignesSelectionnees = avoir.lignes.filter(l => l.selected);
        
        if (lignesSelectionnees.length === 0) {
            showError("Sélectionnez au moins une ligne pour l'avoir");
            return;
        }

        setSaving(true);

        try {
            const userStr = localStorage.getItem("user");
            const user = JSON.parse(userStr);
            const token = localStorage.getItem("token");

            const response = await fetch(
                `${API_BASE_URL}/factures.php`,
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'avoir',
                        id_facture: id,
                        motif: avoir.motif,
                        lignes: lignesSelectionnees.map(l => ({
                            id_produit: l.id_produit,
                            quantite: l.quantite_avoir,
                            prix_unitaire_ht: l.prix_unitaire_ht,
                            tva_taux: l.tva_taux,
                            css_taux: l.css_taux
                        })),
                        id_utilisateur: user.id || user.id_utilisateur
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowAvoirModal(false);
                fetchFacture();
                showSuccess("Avoir créé avec succès");
            } else {
                showError(result.message || "Erreur lors de la création de l'avoir");
            }
        } catch (err) {
            console.error(err);
            showError("Erreur de connexion");
        } finally {
            setSaving(false);
        }
    };

    const company = {
        name: societeInfos?.nom || 'GEOTECH',
        address: societeInfos?.adresse || 'Libreville, Gabon',
        phone: societeInfos?.telephone || '+241 00 00 00 00',
        email: societeInfos?.email || 'contact@geotech.com',
        site_web: societeInfos?.site_web || 'www.geotech.com',
        rc: societeInfos?.rc || 'RC123456',
        nif: societeInfos?.nif || 'NIF123456',
        patente: societeInfos?.patente || 'PAT123456'
    };

    const genererHTMLImpression = () => {
        if (!facture) return '';

        const statutConfig = {
            brouillon: { class: 'draft', label: 'BROUILLON' },
            envoye: { class: 'sent', label: 'ENVOYÉ' },
            accepte: { class: 'accepted', label: 'ACCEPTÉ' },
            refuse: { class: 'refused', label: 'REFUSÉ' },
            payee: { class: 'paid', label: 'PAYÉE' },
            impayee: { class: 'unpaid', label: 'IMPAYÉE' },
            payee_partielle: { class: 'partial', label: 'PAIEMENT PARTIEL' }
        };

        const statut = statutConfig[facture.statut] || statutConfig.brouillon;
        const totalHT = facture.montant_ht || 0;
        const totalTVA = facture.montant_tva || 0;
        const totalCSS = facture.montant_css || 0;
        const totalTTC = facture.net_a_payer || 0;
        const montantRegle = facture.montant_regle || 0;
        const reste = totalTTC - montantRegle;
        const logoSrc = logoBase64;
        const logoText = company.name.substring(0, 2).toUpperCase();

        return `
            <!DOCTYPE html>
            <html lang="fr">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Facture ${facture.numero_facture}</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { 
                        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                        background: #eef2f7;
                        padding: 20px;
                        color: #1e293b;
                        font-size: 11px;
                    }
                    .invoice-container {
                        margin: 0 auto;
                        background: white;
                        border-radius: 12px;
                        overflow: hidden;
                    }
                    
                    .invoice-header {
                        background: white;
                        padding: 5px;
                        border-bottom: 1px solid #e2e8f0;
                    }
                    .header-top {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 15px;
                    }
                    .logo-area {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    }
                    .logo-img {
                        width: auto;
                        height: 60px;
                    }
                    .logo-fallback {
                        width: 50px;
                        height: 50px;
                        background: linear-gradient(135deg, #1a3c5e, #2c5a7a);
                        border-radius: 10px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 20px;
                        font-weight: bold;
                        color: white;
                    }
                    .company-name {
                        font-size: 16px;
                        font-weight: 700;
                        color: #1a3c5e;
                    }
                    .invoice-title {
                        text-align: right;
                    }
                    .invoice-title h2 {
                        font-size: 18px;
                        font-weight: 700;
                        color: #1a3c5e;
                        letter-spacing: 1px;
                        margin-bottom: 4px;
                    }
                    .invoice-number {
                        font-size: 10px;
                        color: #64748b;
                    }
                    .status-badge {
                        display: inline-block;
                        padding: 3px 10px;
                        border-radius: 20px;
                        font-size: 9px;
                        font-weight: 600;
                        margin-top: 6px;
                    }
                    .status-paid { background: #d1fae5; color: #065f46; }
                    .status-unpaid { background: #fee2e2; color: #991b1b; }
                    .status-partial { background: #fef3c7; color: #92400e; }
                    .status-draft { background: #f1f5f9; color: #475569; }
                    
                    .company-details {
                        display: flex;
                        justify-content: space-between;
                        font-size: 9px;
                        color: #64748b;
                        padding: 5px;
                        border-top: 1px solid #e2e8f0;
                    }
                    
                    .invoice-body {
                        padding: 5px 0px;
                    }
                    
                    .client-card {
                        background: #f8fafc;
                        border-radius: 10px;
                        padding: 15px;
                        margin-bottom: 20px;
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        border: 1px solid #e2e8f0;
                    }
                    .info-label {
                        font-size: 9px;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        color: #64748b;
                        margin-bottom: 6px;
                    }
                    .client-name {
                        font-size: 14px;
                        font-weight: 700;
                        color: #0f172a;
                        margin-bottom: 6px;
                    }
                    .client-details {
                        font-size: 10px;
                        color: #475569;
                        line-height: 1.4;
                    }
                    .date-item {
                        margin-bottom: 6px;
                    }
                    .date-label {
                        font-size: 8px;
                        color: #64748b;
                    }
                    .date-value {
                        font-size: 11px;
                        font-weight: 600;
                        color: #0f172a;
                    }
                    
                    .products-table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 15px 0;
                        font-size: 10px;
                    }
                    .products-table th {
                        background: #f8fafc;
                        padding: 8px 10px;
                        text-align: left;
                        font-weight: 600;
                        color: #1e293b;
                        border-bottom: 1.5px solid #e2e8f0;
                    }
                    .products-table td {
                        padding: 8px 10px;
                        border-bottom: 1px solid #e2e8f0;
                        color: #334155;
                    }
                    .text-right { text-align: right; }
                    .text-center { text-align: center; }
                    
                    .totals {
                        display: flex;
                        justify-content: flex-end;
                        margin-top: 15px;
                    }
                    .totals-card {
                        width: 280px;
                        background: #f8fafc;
                        border-radius: 10px;
                        padding: 15px;
                    }
                    .total-line {
                        display: flex;
                        justify-content: space-between;
                        padding: 5px 0;
                        font-size: 10px;
                        border-bottom: 1px dashed #e2e8f0;
                    }
                    .total-line:last-child {
                        border-bottom: none;
                    }
                    .total-line.highlight {
                        background: #eef2ff;
                        margin: 8px -15px;
                        padding: 8px 15px;
                        font-weight: 600;
                    }
                    .total-line.final {
                        background: #fef2f2;
                        margin-top: 8px;
                        padding: 8px 15px;
                        border-radius: 8px;
                    }
                    .amount-paid { color: #10b981; font-weight: 600; }
                    .amount-remaining { color: #ef4444; font-weight: 700; font-size: 13px; }
                    
                    .payments-section {
                        margin-top: 20px;
                        background: #f8fafc;
                        border-radius: 10px;
                        padding: 15px;
                    }
                    .payment-title {
                        font-size: 11px;
                        font-weight: 600;
                        margin-bottom: 10px;
                        color: #1e293b;
                    }
                    .payment-item {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        padding: 8px;
                        background: white;
                        border-radius: 8px;
                        margin-bottom: 6px;
                        font-size: 10px;
                    }
                    
                    .notes-box {
                        margin-top: 15px;
                        padding: 10px;
                        background: #fffbeb;
                        border-left: 3px solid #f59e0b;
                        border-radius: 6px;
                        font-size: 9px;
                    }
                    
                    .signature-section {
                        margin-top: 25px;
                        padding-top: 15px;
                        border-top: 1px solid #e2e8f0;
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-end;
                    }
                    .signature-box {
                        text-align: center;
                    }
                    .signature-line {
                        width: 150px;
                        height: 1px;
                        background: #1a3c5e;
                        margin-bottom: 6px;
                    }
                    .signature-label {
                        font-size: 8px;
                        color: #64748b;
                    }
                    .stamp-box {
                        text-align: center;
                    }
                    .stamp-circle {
                        width: 60px;
                        height: 60px;
                        border: 1.5px dashed #1a3c5e;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 8px;
                        color: #1a3c5e;
                        margin-bottom: 5px;
                    }
                    
                    .invoice-footer {
                        background: #f8fafc;
                        padding: 12px 25px;
                        text-align: center;
                        font-size: 8px;
                        color: #64748b;
                        border-top: 1px solid #e2e8f0;
                    }
                    
                    @media print {
                        body { background: white; padding: 0; }
                        .invoice-container { box-shadow: none; border-radius: 0; }
                    }
                </style>
            </head>
            <body>
                <div class="invoice-container">
                    <div class="invoice-header">
                        <div class="header-top">
                            <div class="logo-area">
                                ${logoSrc ? `<img src="${logoSrc}" alt="Logo" class="logo-img" />` : `<div class="logo-fallback">${logoText}</div>`}
                            </div>
                            <div class="invoice-title">
                                <h2>FACTURE</h2>
                                <div class="invoice-number">N° ${facture.numero_facture}</div>
                                <div class="status-badge status-${statut.class === 'paid' ? 'paid' : statut.class === 'unpaid' ? 'unpaid' : statut.class === 'partial' ? 'partial' : 'draft'}">${statut.label}</div>
                            </div>
                        </div>
                        <div class="company-details">
                            <table style="width: 100%; border: 0px;">
                                <tr>
                                    <td style="padding: 6px 8px; border: 0px solid #e2e8f0; width: 50%;">
                                        <strong>Tél:</strong> ${company.phone}
                                    </td>
                                    <td style="padding: 6px 8px; border: 0px solid #e2e8f0; width: 50%;">
                                        <strong>Adresse:</strong> ${company.address}
                                    </td>
                                </tr>
                                <tr>
                                    <td style="padding: 6px 8px; border: 0px solid #e2e8f0;">
                                        <strong>Email:</strong> ${company.email}
                                    </td>
                                    <td style="padding: 6px 8px; border: 0px solid #e2e8f0;">
                                        <strong>Site web:</strong> ${company.site_web}
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>
                    
                    <div class="invoice-body">
                        <div class="client-card">
                            <div>
                                <div class="info-label">CLIENT</div>
                                <div class="client-name">${facture.client_nom || 'Client non spécifié'}</div>
                                <div class="client-details">
                                    <div>Code: ${facture.code_client || ''}</div>
                                    ${facture.client_telephone ? `<div> ${facture.client_telephone}</div>` : ''}
                                    ${facture.client_email ? `<div>✉ ${facture.client_email}</div>` : ''}
                                    ${facture.client_adresse ? `<div> ${facture.client_adresse}</div>` : ''}
                                </div>
                            </div>
                            <div class="dates">
                                <div class="date-item">
                                    <div class="date-label">Date d'émission</div>
                                    <div class="date-value">${formatDate(facture.date_facture)}</div>
                                </div>
                                <div class="date-item">
                                    <div class="date-label">Date d'échéance</div>
                                    <div class="date-value">${formatDate(facture.date_echeance)}</div>
                                </div>
                            </div>
                        </div>
                        
                        <table class="products-table">
                            <thead>
                                <tr>
                                    <th>Réf.</th>
                                    <th>Désignation</th>
                                    <th class="text-center">Qté</th>
                                    <th class="text-right">Prix HT</th>
                                    <th class="text-center">Remise</th>
                                    <th class="text-right">Total HT</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${facture.lignes?.map(ligne => `
                                    <tr>
                                        <td><span style="font-family: monospace;">${ligne.reference || ''}</span></td>
                                        <td>${ligne.designation || ''}</td>
                                        <td class="text-center">${ligne.quantite || 0}</td>
                                        <td class="text-right">${formatCurrency(ligne.prix_unitaire_ht || 0)}</span></td>
                                        <td class="text-center">${ligne.remise ? `${ligne.remise}%` : '-'}</span></td>
                                        <td class="text-right"><strong>${formatCurrency(ligne.montant_ligne_ht || 0)}</strong></span></td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>
                        
                        <div class="totals">
                            <div class="totals-card">
                                <div class="total-line">
                                    <span>Total HT</span>
                                    <span><strong>${formatCurrency(totalHT)}</strong></span>
                                </div>
                                ${taxes.tva_active && totalTVA > 0 ? `
                                <div class="total-line">
                                    <span>TVA (${taxes.tva_taux}%)</span>
                                    <span>${formatCurrency(totalTVA)}</span>
                                </div>
                                ` : ''}
                                ${taxes.css_active && totalCSS > 0 ? `
                                <div class="total-line">
                                    <span>CSS (${taxes.css_taux}%)</span>
                                    <span>${formatCurrency(totalCSS)}</span>
                                </div>
                                ` : ''}
                                ${facture.remise_globale > 0 ? `
                                <div class="total-line">
                                    <span>Remise globale</span>
                                    <span style="color: #10b981;">-${formatCurrency(facture.remise_globale)}</span>
                                </div>
                                ` : ''}
                                <div class="total-line highlight">
                                    <span><strong>TOTAL TTC</strong></span>
                                    <span><strong style="color: #1a3c5e;">${formatCurrency(totalTTC)}</strong></span>
                                </div>
                                <div class="total-line">
                                    <span>Déjà réglé</span>
                                    <span class="amount-paid">${formatCurrency(montantRegle)}</span>
                                </div>
                                <div class="total-line final">
                                    <span><strong>Reste à payer</strong></span>
                                    <span class="amount-remaining">${formatCurrency(reste)}</span>
                                </div>
                            </div>
                        </div>
                        
                        <div class="signature-section">
                            <div class="signature-box">
                                <div class="signature-line"></div>
                                <div class="signature-label">Signature client</div>
                            </div>
                            <div class="signature-box">
                                <div class="signature-line"></div>
                                <div class="signature-label">Signature et cachet</div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="invoice-footer">
                        <div>Merci de votre confiance — Règlement à effectuer avant la date d'échéance</div>
                        <div style="margin-top: 4px;">${company.name} • ${company.phone} • ${company.email}</div>
                    </div>
                </div>
            </body>
            </html>
        `;
    };

    const handlePrint = () => {
        const htmlContent = genererHTMLImpression();
        
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();
        
        iframe.contentWindow.onload = () => {
            iframe.contentWindow.print();
            setTimeout(() => {
                if (document.body.contains(iframe)) document.body.removeChild(iframe);
            }, 1000);
        };
        
        showSuccess("Document envoyé à l'impression");
    };

    const generatePDF = () => {
        setGeneratingPDF(true);
        try {
            const htmlContent = genererHTMLImpression();
            
            const iframe = document.createElement('iframe');
            iframe.style.position = 'absolute';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = 'none';
            document.body.appendChild(iframe);
            
            const iframeDoc = iframe.contentWindow.document;
            iframeDoc.open();
            iframeDoc.write(htmlContent);
            iframeDoc.close();
            
            iframe.contentWindow.onload = () => {
                iframe.contentWindow.print();
                setTimeout(() => {
                    if (document.body.contains(iframe)) document.body.removeChild(iframe);
                }, 1000);
            };
            
            showSuccess("Document généré avec succès ✅");
        } catch (error) {
            console.error("Erreur PDF:", error);
            showError(`Erreur: ${error.message}`);
        } finally {
            setGeneratingPDF(false);
        }
    };

   const getStatutBadge = (statut) => {
    const config = STATUT_FACTURE[statut] || STATUT_FACTURE.brouillon;
    
    // Couleurs personnalisées selon le statut
    const colorMap = {
        brouillon: { bg: '#64748b', gradient: 'linear-gradient(135deg, #64748b, #475569)' },
        envoye: { bg: '#3b82f6', gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)' },
        accepte: { bg: '#10b981', gradient: 'linear-gradient(135deg, #10b981, #059669)' },
        refuse: { bg: '#ef4444', gradient: 'linear-gradient(135deg, #ef4444, #dc2626)' },
        payee: { bg: '#10b981', gradient: 'linear-gradient(135deg, #10b981, #059669)' },
        impayee: { bg: '#ef4444', gradient: 'linear-gradient(135deg, #ef4444, #dc2626)' },
        payee_partielle: { bg: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b, #d97706)' },
        annulee: { bg: '#dc2626', gradient: 'linear-gradient(135deg, #dc2626, #b91c1c)' },
        converti_facture: { bg: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }
    };
    
    const colors = colorMap[statut] || colorMap.brouillon;
    
    return (
        <span className="status-badge" style={{ 
            background: colors.gradient,
            color: 'white',
            padding: '0.5rem 1rem',
            borderRadius: '30px',
            fontSize: '0.75rem',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            boxShadow: `0 4px 12px ${colors.bg}40`,
            letterSpacing: '0.3px'
        }}>
            <i className={`${config.icon}`} style={{ fontSize: '0.9rem' }}></i>
            {config.label}
        </span>
    );
};
    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false, loading = false }) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}
        >
            <Button
                variant={variant}
                onClick={onClick}
                disabled={disabled || loading}
                className="action-btn"
            >
                {loading ? <Spinner size="sm" /> : <i className={icon}></i>}
            </Button>
        </OverlayTrigger>
    );

    if (loading && !facture) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement de la facture...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !facture) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error || "Facture non trouvée"}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={() => navigate(-1)}>
                                    <i className="ri-arrow-left-line me-1"></i>Retour
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const reste = facture.net_a_payer - (facture.montant_regle || 0);

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}
                    
                    {showErrorAlert && (
                        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {errorMessage}
                        </Alert>
                    )}

                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-file-list-3-line fs-20 text-primary"></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Détail de la facture</h4>
                                    <p className="text-muted small mb-0">Consultez et gérez les informations de la facture</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <ActionButton
                                variant="outline-secondary"
                                icon="ri-arrow-left-line"
                                onClick={() => navigate(-1)}
                                tooltip="Retour"
                            />
                            <ActionButton
                                variant="outline-primary"
                                icon="ri-printer-line"
                                onClick={handlePrint}
                                tooltip="Imprimer"
                            />
                            <ActionButton
                                variant="outline-danger"
                                icon="ri-file-pdf-line"
                                onClick={generatePDF}
                                tooltip="PDF"
                                loading={generatingPDF}
                                disabled={generatingPDF}
                            />
                        </div>
                    </div>

                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="p-4">
                            <Row className="align-items-center">
                                <Col md={4}>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                                            <i className="ri-barcode-line text-primary fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted">N° Facture</small>
                                            <h4 className="mb-0">{facture.numero_facture}</h4>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-success bg-opacity-10 rounded-circle p-3 me-3">
                                            <i className="ri-calendar-line text-success fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted">Date</small>
                                            <h5 className="mb-0">{formatDate(facture.date_facture)}</h5>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                                            <i className="ri-time-line text-warning fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted">Échéance</small>
                                            <h5 className={`mb-0 ${new Date(facture.date_echeance) < new Date() ? 'text-danger' : ''}`}>
                                                {formatDate(facture.date_echeance)}
                                            </h5>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={2}>
                                    <div className="text-end">{getStatutBadge(facture.statut)}</div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <Row className="g-4 mb-4">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0"><i className="ri-building-line me-2"></i>Informations société</h6>
                                </Card.Header>
                                <Card.Body>
                                    <h5 className="fw-bold text-primary mb-3">{company.name}</h5>
                                    <p className="mb-1"><i className="ri-map-pin-line me-2"></i>{company.address}</p>
                                    <p className="mb-1"><i className="ri-phone-line me-2"></i>{company.phone}</p>
                                    <p className="mb-1"><i className="ri-mail-line me-2"></i>{company.email}</p>
                                    <p className="mb-0"><i className="ri-global-line me-2"></i>{company.site_web}</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0"><i className="ri-user-line me-2"></i>Informations client</h6>
                                </Card.Header>
                                <Card.Body>
                                    <h5 className="fw-bold mb-3">{facture.client_nom}</h5>
                                    <p className="mb-1"><i className="ri-barcode-line me-2"></i>Code: {facture.code_client}</p>
                                    {facture.client_telephone && <p className="mb-1"><i className="ri-phone-line me-2"></i>{facture.client_telephone}</p>}
                                    {facture.client_email && <p className="mb-1"><i className="ri-mail-line me-2"></i>{facture.client_email}</p>}
                                    {facture.client_adresse && <p className="mb-0"><i className="ri-map-pin-line me-2"></i>{facture.client_adresse}</p>}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Card className="border-0 shadow-sm rounded-3 mb-4 products-card">
    <Card.Header className="bg-transparent border-0 pt-4 pb-3">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
                <div className="section-icon-small">
                    <i className="ri-shopping-cart-line text-primary"></i>
                </div>
                <h6 className="mb-0 fw-semibold">Détail des produits</h6>
            </div>
            <div className="d-flex align-items-center gap-2">
                <Badge bg="light" className="px-3 py-2 rounded-pill">
                    <i className="ri-stack-line me-1"></i>
                    {facture.lignes?.length || 0} article(s)
                </Badge>
                <Badge bg="light" className="px-3 py-2 rounded-pill">
                    <i className="ri-calculator-line me-1"></i>
                    Total: {facture.lignes?.reduce((acc, l) => acc + (l.quantite || 0), 0)} unités
                </Badge>
            </div>
        </div>
    </Card.Header>
    <Card.Body className="p-0">
        <div className="table-responsive">
            <Table className="mb-0 align-middle products-table">
                <thead>
                    <tr>
                        <th className="ps-4" style={{ width: '15%' }}>Référence</th>
                        <th style={{ width: '40%' }}>Désignation</th>
                        <th className="text-center" style={{ width: '10%' }}>Qté</th>
                        <th className="text-end" style={{ width: '15%' }}>Prix HT</th>
                        <th className="text-center" style={{ width: '10%' }}>Remise</th>
                        <th className="text-end pe-4" style={{ width: '15%' }}>Total HT</th>
                    </tr>
                </thead>
                <tbody>
                    {facture.lignes?.map((ligne, index) => (
                        <tr key={index} className="product-row">
                            <td className="ps-4">
                                <span className="reference-badge">
                                    <i className="ri-barcode-line me-1 fs-10"></i>
                                    {ligne.reference}
                                </span>
                            </td>
                            <td>
                                <div className="product-info">
                                    <span className="product-name fw-semibold">{ligne.designation}</span>
                                    {ligne.description && (
                                        <small className="product-description text-muted d-block">{ligne.description}</small>
                                    )}
                                </div>
                            </td>
                            <td className="text-center">
                                <span className="quantity-badge">
                                    {formatNumber(ligne.quantite)}
                                </span>
                            </td>
                            <td className="text-end">
                                <span className="price-value">{formatCurrency(ligne.prix_unitaire_ht)}</span>
                            </td>
                            <td className="text-center">
                                {ligne.remise ? (
                                    <span className="remise-badge">
                                        <i className="ri-discount-percent-line me-1"></i>
                                        {ligne.remise}%
                                    </span>
                                ) : (
                                    <span className="text-muted">-</span>
                                )}
                            </td>
                            <td className="text-end pe-4">
                                <span className="total-value fw-bold text-primary">
                                    {formatCurrency(ligne.montant_ligne_ht)}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot className="bg-light">
    <tr>
        <td colSpan="5" className="text-end fw-semibold ps-4">Total HT :</td>
        <td className="text-end pe-4 fw-bold text-primary fs-5">{formatCurrency(facture.montant_ht)}</td>
    </tr>
    {facture.remise_globale > 0 && (
        <tr className="text-success">
            <td colSpan="5" className="text-end ps-4">Remise globale :</td>
            <td className="text-end pe-4 fw-semibold">-{formatCurrency(facture.remise_globale)}</td>
        </tr>
    )}
    {facture.montant_tva > 0 && (
        <tr>
            <td colSpan="5" className="text-end ps-4">TVA ({taxes.tva_taux}%) :</td>
            <td className="text-end pe-4 text-warning fw-semibold">{formatCurrency(facture.montant_tva)}</td>
        </tr>
    )}
    {facture.montant_css > 0 && (
        <tr>
            <td colSpan="5" className="text-end ps-4">CSS ({taxes.css_taux}%) :</td>
            <td className="text-end pe-4 text-info fw-semibold">{formatCurrency(facture.montant_css)}</td>
        </tr>
    )}
    <tr className="border-top">
        <td colSpan="5" className="text-end fw-bold fs-6 ps-4">TOTAL TTC :</td>
        <td className="text-end pe-4 fw-bold text-primary fs-4">{formatCurrency(facture.net_a_payer)}</td>
    </tr>
</tfoot>
            </Table>
        </div>
    </Card.Body>
</Card>

                    <Row className="g-4 mb-4">
                      <Col md={6}>
    <Card className="border-0 shadow-sm rounded-3 recap-card">
        <Card.Header className="bg-transparent border-0 pt-4 pb-3">
            <div className="d-flex align-items-center gap-2">
                <div className="recap-icon">
                    <i className="ri-calculator-line text-primary"></i>
                </div>
                <h6 className="mb-0 fw-semibold">Récapitulatif financier</h6>
            </div>
        </Card.Header>
        <Card.Body className="pt-0">
            <div className="recap-container">
                {/* SECTION 1 : BASE HT */}
                <div className="recap-section">
                    <div className="recap-line">
                        <span className="recap-label">Total HT</span>
                        <span className="recap-value fw-bold">{formatCurrency(facture.montant_ht)}</span>
                    </div>

                    {facture.remise_globale > 0 && (
                        <div className="recap-line text-success">
                            <span className="recap-label">
                                <i className="ri-discount-percent-line me-1"></i>
                                Remise globale
                            </span>
                            <span className="recap-value fw-semibold">-{formatCurrency(facture.remise_globale)}</span>
                        </div>
                    )}

                    {(facture.remise_globale > 0) && (
                        <div className="recap-line recap-subtotal">
                            <span className="recap-label">Sous-total</span>
                            <span className="recap-value">
                                {formatCurrency((facture.montant_ht || 0) - (facture.remise_globale || 0))}
                            </span>
                        </div>
                    )}
                </div>

                {/* SECTION 2 : AVANCE / ACOMPTE */}
                {facture.avance_reglement > 0 && (
                    <div className="recap-section recap-avance-section">
                        <div className="recap-line text-info">
                            <span className="recap-label">
                                <i className="ri-money-euro-circle-line me-1"></i>
                                Avance / Acompte
                            </span>
                            <span className="recap-value fw-semibold">-{formatCurrency(facture.avance_reglement)}</span>
                        </div>
                    </div>
                )}

                {/* SECTION 3 : TAXES */}
                {(taxes.tva_active && facture.montant_tva > 0) || (taxes.css_active && facture.montant_css > 0) ? (
                    <div className="recap-section">
                        {taxes.tva_active && facture.montant_tva > 0 && (
                            <div className="recap-line">
                                <span className="recap-label">TVA ({taxes.tva_taux}%)</span>
                                <span className="recap-value text-warning">+{formatCurrency(facture.montant_tva)}</span>
                            </div>
                        )}

                        {taxes.css_active && facture.montant_css > 0 && (
                            <div className="recap-line">
                                <span className="recap-label">CSS ({taxes.css_taux}%)</span>
                                <span className="recap-value text-info">+{formatCurrency(facture.montant_css)}</span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="recap-line text-muted">
                        <span className="recap-label">Taxes</span>
                        <span className="recap-value">Exonéré</span>
                    </div>
                )}

                <div className="recap-divider"></div>

                {/* SECTION 4 : TOTAL TTC */}
                <div className="recap-section">
                    <div className="recap-line recap-total">
                        <span className="recap-label fw-bold">TOTAL TTC</span>
                        <span className="recap-value fw-bold text-primary fs-3">{formatCurrency(facture.net_a_payer)}</span>
                    </div>
                </div>

                {/* SECTION 5 : TOTAL RÉGLÉ (Avance + Paiements complémentaires) */}
                <div className="recap-section">
                    <div className="recap-line">
                        <span className="recap-label">
                            <i className="ri-bank-card-line me-1"></i>
                            Total réglé
                        </span>
                        <span className="recap-value text-success fw-bold">
                            {formatCurrency(facture.montant_regle || 0)}
                        </span>
                    </div>
                    
                    {/* Détail du total réglé */}
                    <div className="recap-line ps-3">
                        <span className="recap-label small">
                            <i className="ri-money-euro-circle-line me-1"></i>
                            Dont avance
                        </span>
                        <span className="recap-value small text-info">
                            {formatCurrency(facture.avance_reglement || 0)}
                        </span>
                    </div>
                    
                    {((facture.montant_regle || 0) - (facture.avance_reglement || 0)) > 0 && (
                        <div className="recap-line ps-3">
                            <span className="recap-label small">
                                <i className="ri-bank-card-line me-1"></i>
                                Dont paiements
                            </span>
                            <span className="recap-value small text-success">
                                {formatCurrency((facture.montant_regle || 0) - (facture.avance_reglement || 0))}
                            </span>
                        </div>
                    )}
                </div>

                <div className="recap-divider-light"></div>

                {/* SECTION 6 : RESTE À PAYER */}
                <div className="recap-section">
                    <div className="recap-line recap-restant">
                        <span className="recap-label fw-semibold">Reste à payer</span>
                        <span className={`recap-value fw-bold fs-2 ${reste > 0 ? 'text-danger' : 'text-success'}`}>
                            {formatCurrency(reste)}
                        </span>
                    </div>

                    {/* Barre de progression */}
                    {facture.net_a_payer > 0 && (
                        <div className="payment-progress mt-2">
                            <div className="progress-label d-flex justify-content-between small mb-1">
                                <span className="text-muted">Taux de règlement</span>
                                <span className="fw-semibold">{Math.round(((facture.montant_regle || 0) / facture.net_a_payer) * 100)}%</span>
                            </div>
                            <div className="progress" style={{ height: '8px', borderRadius: '4px', background: '#e2e8f0' }}>
                                <div 
                                    className="progress-bar bg-success" 
                                    style={{ 
                                        width: `${Math.min(((facture.montant_regle || 0) / facture.net_a_payer) * 100, 100)}%`,
                                        borderRadius: '4px',
                                        transition: 'width 0.5s ease'
                                    }}
                                ></div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Boutons d'action */}
            <div className="d-grid gap-2 mt-4">
                {facture.statut !== 'payee' && facture.statut !== 'annulee' && (
                    <Button 
                        variant="success" 
                        className="rounded-pill py-2 btn-encaisser"
                        onClick={() => { setPaiement({...paiement, montant: reste}); setShowPaiementModal(true); }} 
                        disabled={reste <= 0}
                    >
                        <i className="ri-bank-card-line me-2"></i> 
                        Encaisser {reste > 0 && `(${formatCurrency(reste)})`}
                    </Button>
                )}
                <Button 
                    variant="outline-warning" 
                    className="rounded-pill py-2"
                    onClick={() => setShowAvoirModal(true)}
                >
                    <i className="ri-file-copy-line me-2"></i> 
                    Créer un avoir
                </Button>
                {facture.statut === 'payee' && (
                    <div className="text-center text-success small mt-2 py-2 bg-success bg-opacity-10 rounded-3">
                        <i className="ri-checkbox-circle-line me-1"></i>
                        Facture entièrement réglée
                    </div>
                )}
            </div>
        </Card.Body>
    </Card>
</Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0"><i className="ri-history-line me-2"></i>Historique des paiements</h6>
                                </Card.Header>
                                <Card.Body>
                                    {facture.paiements?.length > 0 ? (
    <div className="paiements-list">
        {facture.paiements.map((p, index) => {
            const mode = MODE_REGLEMENT.find(m => m.value === p.mode_reglement);
            return (
                <div key={index} className="paiement-item">
                    <div className="d-flex justify-content-between align-items-start">
                        <div className="d-flex align-items-center gap-3">
                            <div className="paiement-icon" style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '14px',
                                background: `linear-gradient(135deg, ${p.statut === 'valide' ? '#10b98120' : '#f59e0b20'}, ${p.statut === 'valide' ? '#05966920' : '#d9770620'})`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <i className={`${mode?.icon || 'ri-bank-line'} ${p.statut === 'valide' ? 'text-success' : 'text-warning'}`} style={{ fontSize: '1.5rem' }}></i>
                            </div>
                            <div>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <h6 className="mb-0 fw-bold">{mode?.label || p.mode_reglement}</h6>
                                    <Badge 
                                        bg={p.statut === 'valide' ? 'success' : 'warning'} 
                                        className="px-2 py-1 rounded-pill"
                                        style={{ fontSize: '0.65rem', fontWeight: '500' }}
                                    >
                                        <i className={`${p.statut === 'valide' ? 'ri-check-line' : 'ri-time-line'} me-1`}></i>
                                        {p.statut === 'valide' ? 'Validé' : 'En attente'}
                                    </Badge>
                                </div>
                                <div className="d-flex align-items-center gap-3 mt-1 flex-wrap">
                                    <small className="text-muted d-flex align-items-center gap-1">
                                        <i className="ri-calendar-line"></i>
                                        {formatDate(p.date_reglement)}
                                    </small>
                                    {p.reference && (
                                        <small className="text-muted d-flex align-items-center gap-1">
                                            <i className="ri-file-text-line"></i>
                                            Réf: {p.reference}
                                        </small>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="text-end">
                            <div className="fw-bold text-success fs-5">
                                {formatCurrency(p.montant)}
                            </div>
                            {p.mode_reglement === 'cheque' && p.reference && (
                                <small className="text-muted">N° chèque: {p.reference}</small>
                            )}
                            {p.mode_reglement === 'virement' && p.reference && (
                                <small className="text-muted">Réf virement: {p.reference}</small>
                            )}
                        </div>
                    </div>
                    {p.commentaire && (
                        <div className="mt-2 pt-2 border-top">
                            <small className="text-muted d-flex align-items-center gap-1">
                                <i className="ri-chat-1-line"></i>
                                {p.commentaire}
                            </small>
                        </div>
                    )}
                </div>
            );
        })}
    </div>
) : (
    <div className="text-center py-5">
        <div className="empty-paiement" style={{
            width: '80px',
            height: '80px',
            margin: '0 auto 1rem',
            background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <i className="ri-bank-card-line fs-1 text-muted"></i>
        </div>
        <h6 className="fw-semibold mb-2">Aucun paiement enregistré</h6>
        <p className="text-muted small mb-0">Les paiements effectués apparaîtront ici</p>
    </div>
)}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {facture.notes && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4">
                            <Card.Header className="bg-transparent border-0 pt-4">
                                <h6 className="mb-0"><i className="ri-file-text-line me-2"></i>Notes</h6>
                            </Card.Header>
                            <Card.Body>
                                <p className="mb-0">{facture.notes}</p>
                            </Card.Body>
                        </Card>
                    )}
                </div>
                <Footer />
            </div>

            {/* Modal de paiement */}
            <Modal show={showPaiementModal} onHide={() => setShowPaiementModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0" style={{background:'#1DACCE'}}>
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-3">
                                <i className="ri-bank-card-line text-white" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold">Enregistrer un encaissement</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handlePaiementSubmit}>
                    <Modal.Body className="pt-2">
                       
                        <Form.Group className="mb-3">
                            <Form.Label>Date de paiement</Form.Label>
                            <Form.Control type="date" value={paiement.date_reglement} onChange={(e) => setPaiement({...paiement, date_reglement: e.target.value})} required className="rounded-pill" />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Montant</Form.Label>
                            <InputGroup>
                                <Form.Control type="number" step="0.01" min="0.01" max={reste} value={paiement.montant} onChange={(e) => setPaiement({...paiement, montant: e.target.value})} required className="rounded-pill" />
                                <InputGroup.Text className="rounded-pill">FCFA</InputGroup.Text>
                            </InputGroup>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Mode de règlement</Form.Label>
                            <Form.Select value={paiement.mode_reglement} onChange={(e) => setPaiement({...paiement, mode_reglement: e.target.value})} required className="rounded-pill">
                                {MODE_REGLEMENT.map(mode => (<option key={mode.value} value={mode.value}>{mode.label}</option>))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Référence (optionnel)</Form.Label>
                            <Form.Control type="text" placeholder="N° chèque, référence virement..." value={paiement.reference} onChange={(e) => setPaiement({...paiement, reference: e.target.value})} className="rounded-pill" />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 justify-content-center">
                        <Button variant="outline-secondary" onClick={() => setShowPaiementModal(false)} className="px-4 rounded-pill">
                            Annuler
                        </Button>
                        <Button variant="success" type="submit" disabled={saving} className="px-5 rounded-pill">
                            {saving ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : <><i className="ri-check-line me-2"></i>Enregistrer</>}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal Avoir */}
            <Modal show={showAvoirModal} onHide={() => setShowAvoirModal(false)} size="lg" centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0" style={{background:'#1DACCE'}}>
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-3">
                                <i className="ri-file-copy-line text-warning" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold">Créer un avoir</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAvoirSubmit}>
                    <Modal.Body className="pt-2">
                        <Alert variant="warning" className="mb-4 rounded-3">
                            <i className="ri-information-line me-2"></i> L'avoir sera émis pour les lignes sélectionnées ci-dessous.
                        </Alert>

                        <Form.Group className="mb-3">
                            <Form.Label>Motif de l'avoir</Form.Label>
                            <Form.Control as="textarea" rows={2} value={avoir.motif} onChange={(e) => setAvoir({...avoir, motif: e.target.value})} placeholder="Raison de l'avoir..." required className="rounded-3" />
                        </Form.Group>

                        <h6 className="mb-3">Sélectionnez les lignes à avoir :</h6>
                        <div className="table-responsive">
                            <Table striped size="sm" className="align-middle">
                                <thead>
                                    <tr>
                                        <th><Form.Check type="checkbox" checked={avoir.lignes.every(l => l.selected)} onChange={(e) => { const checked = e.target.checked; setAvoir({...avoir, lignes: avoir.lignes.map(l => ({...l, selected: checked}))}); }} /></th>
                                        <th>Produit</th>
                                        <th className="text-center">Quantité</th>
                                        <th className="text-end">Prix HT</th>
                                        <th className="text-end">Total HT</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {avoir.lignes.map((ligne, index) => (
                                        <tr key={index}>
                                            <td><Form.Check type="checkbox" checked={ligne.selected} onChange={(e) => { const newLignes = [...avoir.lignes]; newLignes[index].selected = e.target.checked; setAvoir({...avoir, lignes: newLignes}); }} /></td>
                                            <td style={{minWidth:'200px'}}><div className="fw-semibold">{ligne.designation}</div><small className="text-muted">{ligne.reference}</small></td>
                                            <td className="text-center"><Form.Control type="number" min="1" max={ligne.quantite} size="sm" style={{ width: '80px', margin: '0 auto' }} value={ligne.quantite_avoir} disabled={!ligne.selected} onChange={(e) => { const newLignes = [...avoir.lignes]; newLignes[index].quantite_avoir = parseInt(e.target.value) || 1; setAvoir({...avoir, lignes: newLignes}); }} className="text-center" /></td>
                                            <td className="text-end">{formatCurrency(ligne.prix_unitaire_ht)}</td>
                                            <td className="text-end">{ligne.selected ? formatCurrency(ligne.prix_unitaire_ht * ligne.quantite_avoir) : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 justify-content-center">
                        <Button variant="outline-secondary" onClick={() => setShowAvoirModal(false)} className="px-4 rounded-pill">
                            Annuler
                        </Button>
                        <Button variant="warning" type="submit" disabled={saving} className="px-5 rounded-pill">
                            {saving ? <><Spinner size="sm" className="me-2" />Création...</> : <><i className="ri-check-line me-2"></i>Créer</>}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <style>{`
                .action-btn {
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-primary:hover {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                }
                .btn-outline-secondary:hover {
                    background: linear-gradient(135deg, #64748b, #475569);
                    border-color: #64748b;
                    color: white;
                }
                .btn-outline-danger:hover {
                    background: linear-gradient(135deg, #ef4444, #dc2626);
                    border-color: #ef4444;
                    color: white;
                }
                .font-monospace {
                    font-family: monospace;
                }
                .rounded-3 {
                    border-radius: 12px !important;
                }
                .rounded-pill {
                    border-radius: 50px !important;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                }



                /* Paiements list styles */
.paiements-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.paiement-item {
    padding: 1.25rem;
    background: white;
    border-radius: 16px;
    transition: all 0.3s ease;
    border: 1px solid #e2e8f0;
    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
}

.paiement-item:hover {
    transform: translateX(5px);
    border-color: #21ACCD;
    box-shadow: 0 8px 20px rgba(33, 172, 205, 0.1);
}

.paiement-icon {
    transition: all 0.3s ease;
}

.paiement-item:hover .paiement-icon {
    transform: scale(1.05);
}

.empty-paiement {
    transition: all 0.3s ease;
    animation: pulse-light 2s infinite;
}

@keyframes pulse-light {
    0%, 100% {
        transform: scale(1);
        opacity: 1;
    }
    50% {
        transform: scale(1.02);
        opacity: 0.9;
    }
}

/* Badge styles */
.badge.bg-success {
    background: linear-gradient(135deg, #10b981, #059669) !important;
}

.badge.bg-warning {
    background: linear-gradient(135deg, #f59e0b, #d97706) !important;
}

/* Responsive */
@media (max-width: 768px) {
    .paiement-item {
        padding: 1rem;
    }
    
    .paiement-item .d-flex {
        flex-direction: column;
        gap: 1rem;
    }
    
    .paiement-item .text-end {
        text-align: left !important;
        width: 100%;
    }
    
    .paiement-icon {
        width: 40px !important;
        height: 40px !important;
    }
    
    .paiement-icon i {
        font-size: 1.2rem !important;
    }
}


/* Recap Card Styles */
.recap-card {
    overflow: hidden;
}

.recap-icon {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #21ACCD20, #1a8ba820);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.recap-container {
    padding: 0.5rem 0;
}

.recap-line {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.6rem 0;
}

.recap-label {
    font-size: 0.85rem;
    color: #64748b;
}

.recap-value {
    font-size: 0.95rem;
    font-weight: 500;
}

.recap-subtotal {
    border-bottom: 1px dashed #e2e8f0;
    margin-bottom: 0.25rem;
}

.recap-divider {
    height: 1px;
    background: linear-gradient(90deg, #e2e8f0, transparent);
    margin: 0.5rem 0;
}

.recap-divider-light {
    height: 1px;
    background: linear-gradient(90deg, #f1f5f9, transparent);
    margin: 0.5rem 0;
}

.recap-total {
    background: linear-gradient(135deg, #f8fafc, #ffffff);
    padding: 0.75rem;
    border-radius: 12px;
    margin: 0.5rem 0;
}

.recap-avance {
    padding-left: 1.5rem;
    font-size: 0.8rem;
}

.recap-restant {
    background: #f8fafc;
    padding: 1rem;
    border-radius: 12px;
    margin-top: 0.5rem;
}

/* Progress bar */
.payment-progress {
    padding: 0.25rem 0;
}

.progress {
    background-color: #e2e8f0;
    box-shadow: inset 0 1px 2px rgba(0,0,0,0.05);
}

.progress-bar {
    transition: width 0.5s ease;
}

/* Bouton encaisser */
.btn-encaisser {
    background: linear-gradient(135deg, #10b981, #059669);
    border: none;
    transition: all 0.3s ease;
}

.btn-encaisser:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
}

.btn-encaisser:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
    .recap-line {
        padding: 0.4rem 0;
    }
    
    .recap-label, .recap-value {
        font-size: 0.8rem;
    }
    
    .recap-total .recap-value {
        font-size: 1.3rem;
    }
    
    .recap-restant .recap-value {
        font-size: 1.5rem;
    }
}
            `}</style>
        </>
    );
}